/**
 * In-form batch usage for Inventory Transfer (current submission only).
 * Used to cap auto-fill and block adding/editing when a batch is fully claimed
 * on another transfer line.
 */

export const BATCH_FULLY_USED_MESSAGE =
  "The full amount for this item batch is already used on another line in this transfer.";

export const BATCH_PARTIAL_REMAINING_MESSAGE =
  "Only the remaining available amount for this item batch can be used; the rest is already on another line in this transfer.";

function roundQty(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return 0;
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}

function parsePositiveAmount(value) {
  const n = parseFloat(value);
  return n > 0 && !Number.isNaN(n) ? n : 0;
}

function toLotCodesArray(lotcodes) {
  if (lotcodes == null || lotcodes === "") return [];
  if (Array.isArray(lotcodes)) return lotcodes;
  if (typeof lotcodes === "object") return [lotcodes];
  const text = lotcodes.toString().trim();
  if (text.charAt(0) === "[") {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) {
      // fall through
    }
  }
  return [];
}

/** Resolve historial batch id from a selected lot or saved lotcode entry. */
export function getTransferHistorialId(entry) {
  if (!entry) return null;
  if (entry.historialId) return String(entry.historialId);
  const h = Array.isArray(entry.historial) ? entry.historial[0] : null;
  if (h?._id) return String(h._id);
  return null;
}

export function getTransferBatchId(entry) {
  if (!entry) return null;
  if (entry.batchId) return String(entry.batchId);
  return null;
}

/** True when two lot entries refer to the same inventory batch. */
export function sameTransferBatch(a, b) {
  if (!a || !b) return false;
  const aHist = getTransferHistorialId(a);
  const bHist = getTransferHistorialId(b);
  if (aHist && bHist) return aHist === bHist;
  // Prefer historialId when only one side has it — avoid false matches on batchId alone
  // when the other side is identified by historial.
  if (aHist || bHist) return false;
  const aBatch = getTransferBatchId(a);
  const bBatch = getTransferBatchId(b);
  if (aBatch && bBatch) return aBatch === bBatch;
  return false;
}

function getAmountFromLotEntry(entry) {
  if (!entry) return 0;
  if (entry.editedAmount != null && entry.editedAmount !== "") {
    return parsePositiveAmount(entry.editedAmount);
  }
  const h = Array.isArray(entry.historial) ? entry.historial[0] : null;
  if (h) {
    const fromH = parsePositiveAmount(h.editedAmount != null ? h.editedAmount : h.amount);
    if (fromH > 0) return fromH;
  }
  return parsePositiveAmount(entry.amount);
}

/**
 * Sum quantity already assigned to a batch on other form rows.
 * Excludes `excludePosition` (the row that opened the modal).
 */
export function getTransferBatchUsedOnForm(submission, components, batchRef, excludePosition) {
  if (!submission || !components || !batchRef) return 0;
  const section = components.find(
    (c) => c.type === "section" && c.defaultLabel === "rawMaterials" && c.direction === "table"
  );
  if (!section) return 0;
  const sectionId = section.section_id;
  const lotComp = components.find(
    (c) => c.type !== "section" && c.section_id == sectionId && c.defaultLabel === "lotcode"
  );
  const qtyComp = components.find(
    (c) => c.type !== "section" && c.section_id == sectionId && c.defaultLabel === "quantity"
  );
  if (!lotComp) return 0;

  const lotField = submission[lotComp.id];
  if (!Array.isArray(lotField)) return 0;

  let sum = 0;
  lotField.forEach((cell, pos) => {
    if (excludePosition != null && Number(pos) === Number(excludePosition)) return;
    const entries = toLotCodesArray(cell);
    entries.forEach((entry) => {
      if (!entry || typeof entry !== "object") return;
      if (!sameTransferBatch(batchRef, entry)) return;
      let amt = getAmountFromLotEntry(entry);
      if (!(amt > 0) && qtyComp && submission[qtyComp.id] != null) {
        const qRaw = submission[qtyComp.id];
        const qVal = Array.isArray(qRaw) ? qRaw[pos] : qRaw;
        amt = parsePositiveAmount(qVal);
      }
      sum += amt;
    });
  });
  return roundQty(sum);
}

/**
 * Sum quantity for a batch already in the modal's selectedLots list.
 * Optionally exclude one selected-lot key (the row being edited).
 */
export function getTransferBatchUsedInSelectedLots(selectedLots, batchRef, excludeKey) {
  if (!Array.isArray(selectedLots) || !batchRef) return 0;
  let sum = 0;
  selectedLots.forEach((lot) => {
    if (!lot || lot.isSeed) return;
    if (excludeKey != null && lot.key === excludeKey) return;
    if (!sameTransferBatch(batchRef, lot)) return;
    sum += parsePositiveAmount(lot.editedAmount);
  });
  return roundQty(sum);
}

/**
 * Total already used for a batch outside the current edit target:
 * other form lines + other selected lots in the modal.
 */
export function getTransferBatchUsedElsewhere({
  submission,
  components,
  position,
  selectedLots,
  batchRef,
  excludeSelectedKey,
}) {
  const onForm = getTransferBatchUsedOnForm(submission, components, batchRef, position);
  const inModal = getTransferBatchUsedInSelectedLots(selectedLots, batchRef, excludeSelectedKey);
  return roundQty(onForm + inModal);
}

/** Remaining transferable amount for a batch given on-hand stock. */
export function getTransferBatchRemaining(stockAmount, usedElsewhere) {
  return Math.max(0, roundQty(parsePositiveAmount(stockAmount) - roundQty(usedElsewhere)));
}

export function roundTransferAllocationAmount(value) {
  return roundQty(value);
}

function normalizeItemName(name) {
  return (name == null ? "" : name).toString().trim().toLowerCase();
}

function readFormRowItemName(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    return (value.itemName || value.item || value.name || "").toString().trim();
  }
  return value.toString().trim();
}

/**
 * Qty already claimed for an item name on other transfer form rows
 * (excludes the row that opened the modal).
 */
export function getTransferItemUsedOnForm(submission, components, itemName, excludePosition) {
  if (!submission || !components || !normalizeItemName(itemName)) return 0;
  const section = components.find(
    (c) => c.type === "section" && c.defaultLabel === "rawMaterials" && c.direction === "table"
  );
  if (!section) return 0;
  const sectionId = section.section_id;
  const itemComp = components.find(
    (c) =>
      c.type !== "section" &&
      c.section_id == sectionId &&
      ["itemName", "item", "ingredient", "name"].includes(c.defaultLabel)
  );
  const lotComp = components.find(
    (c) => c.type !== "section" && c.section_id == sectionId && c.defaultLabel === "lotcode"
  );
  const qtyComp = components.find(
    (c) => c.type !== "section" && c.section_id == sectionId && c.defaultLabel === "quantity"
  );
  if (!lotComp && !qtyComp) return 0;

  const target = normalizeItemName(itemName);
  const lotField = lotComp ? submission[lotComp.id] : null;
  const qtyField = qtyComp ? submission[qtyComp.id] : null;
  const itemField = itemComp ? submission[itemComp.id] : null;
  const len = Math.max(
    Array.isArray(lotField) ? lotField.length : 0,
    Array.isArray(qtyField) ? qtyField.length : 0,
    Array.isArray(itemField) ? itemField.length : 0
  );

  let sum = 0;
  for (let pos = 0; pos < len; pos++) {
    if (excludePosition != null && Number(pos) === Number(excludePosition)) continue;

    const rowItem = readFormRowItemName(Array.isArray(itemField) ? itemField[pos] : null);
    const lotEntries = toLotCodesArray(Array.isArray(lotField) ? lotField[pos] : null);
    const lotItem =
      lotEntries.find((e) => e && typeof e === "object" && (e.item || e.itemName)) || null;
    const effectiveItem = rowItem || readFormRowItemName(lotItem);
    if (normalizeItemName(effectiveItem) !== target) continue;

    let rowAmt = 0;
    lotEntries.forEach((entry) => {
      if (!entry || typeof entry !== "object") return;
      rowAmt += getAmountFromLotEntry(entry);
    });
    if (!(rowAmt > 0) && qtyField != null) {
      const qVal = Array.isArray(qtyField) ? qtyField[pos] : qtyField;
      rowAmt = parsePositiveAmount(qVal);
    }
    sum += rowAmt;
  }
  return roundQty(sum);
}

/** Qty for an item name already picked in the open transfer modal. */
export function getTransferItemUsedInSelectedLots(selectedLots, itemName) {
  if (!Array.isArray(selectedLots) || !normalizeItemName(itemName)) return 0;
  const target = normalizeItemName(itemName);
  let sum = 0;
  selectedLots.forEach((lot) => {
    if (!lot || lot.isSeed) return;
    const name = lot.item || lot.itemName || "";
    if (normalizeItemName(name) !== target) return;
    sum += parsePositiveAmount(lot.editedAmount);
  });
  return roundQty(sum);
}

/** Remaining display amount for an item after other lines + modal selection. */
export function getTransferItemRemainingAmount(
  stockAmount,
  itemName,
  { submission, components, position, selectedLots } = {}
) {
  const usedOnForm = getTransferItemUsedOnForm(submission, components, itemName, position);
  const usedInModal = getTransferItemUsedInSelectedLots(selectedLots, itemName);
  return getTransferBatchRemaining(stockAmount, roundQty(usedOnForm + usedInModal));
}
